/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import {
	APP_TYPES,
	PRESEARCH_UI_SORT_ORDERS,
	SEARCH_CCD_CCH_SAMCAP_CGEN,
	SEARCH_QA_ICONS,
} from '../../../containers/SearchResultsPage/SearchConstants';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchUtils from '../../../utils/SearchUtils';
import FileTypeService from '../../../services/FileTypeService';

type OwnProps = {
	data: Record<string, any>;
	index: number;
	count: number;
	lastCard?: boolean;
	tabIndex?: boolean;
};

type SearchDropDownQuickActionsItemProps = OwnProps & WrappedComponentProps & PropsFromRedux;

class SearchDropDownQuickActionsItem extends ClickBase<SearchDropDownQuickActionsItemProps> {
	searchHostContext: HostContext;
	private analytics: SearchAnalytics;
	quickActionTranslation: string | Record<string, any>;

	constructor(props: SearchDropDownQuickActionsItemProps) {
		super(props);
		this.quickActionTranslation = FileTypeService.fileTypeDataObject?.translations?.preSearchQuickActions || {};
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.quickActionClick = this.quickActionClick.bind(this);
	}

	getTitleId(): string {
		const localeKey = `app.search.presearch.quickActions.${this.props.data?.id}`;
		const titleId = this.quickActionTranslation?.[localeKey];
		return titleId || localeKey;
	}

	quickActionClick() {
		const { actionUrl } = this.props.data;
		this.postAnalyticsEvent('click');
		const {
			hostInfo: { surface, client },
		} = this.searchHostContext;
		const appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(actionUrl);
		let trackingParams = '';
		if (surface === SEARCH_CCD_CCH_SAMCAP_CGEN.ccdHost) {
			trackingParams = `${SEARCH_CCD_CCH_SAMCAP_CGEN.ccdExpressCgenForPreSearchQuickActions}&${SEARCH_CCD_CCH_SAMCAP_CGEN.samcapId}`;
		} else if (client === SEARCH_CCD_CCH_SAMCAP_CGEN.cchClient) {
			trackingParams = SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForPreSearchQuickActions;
		}
		const openUrl = appendedUrl.includes('?')
			? `${appendedUrl}&${trackingParams}`
			: `${appendedUrl}?${trackingParams}`;
		this.onClickUrl(this.searchHostContext, APP_TYPES.SPARK_TEMPLATES, openUrl);
	}

	postAnalyticsEvent(eventType: string) {
		this.analytics.postPreSearchItemAction({
			eventType,
			data: [this.props.data],
			recommendationType: 'popularExpressQuickActions',
			uiSortOrder: PRESEARCH_UI_SORT_ORDERS.popularExpressQuickActions,
			eventIndex: this.props.index,
		});
	}

	keyDownHandler = (event: Record<string, any>) => {
		if (
			event.keyCode === 40 ||
			event.key === 'ArrowDown' ||
			event.keyCode === 38 ||
			event.key === 'ArrowUp' ||
			event.key === 'Tab' ||
			event.which === 9
		) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	onFocusHandler = (event: Record<string, any>) => {
		event.target.setAttribute('aria-current', 'true');
	};

	onBlurHandler = (event: Record<string, any>) => {
		event.target.setAttribute('aria-current', 'false');
	};

	render() {
		const { data, index, theme } = this.props;
		const darkMode = SearchUtils.isDarkMode(theme);
		let iconSource;
		if (darkMode) {
			iconSource = data?.iconDark ? data?.iconDark : SEARCH_QA_ICONS[`${data?.id}_dark`];
		} else {
			iconSource = data?.icon;
		}
		return (
			<div
				id={`search-dropdown-quick-action-item-${index}`}
				className={`search-dropdown-quick-action-item ${this.props.lastCard ? ' last-item' : ''}`}
				onFocus={this.onFocusHandler}
				onBlur={this.onBlurHandler}
				onClick={this.quickActionClick}
				onKeyDown={this.keyDownHandler}
				tabIndex={0}
				role='link'
				aria-current='false'
				aria-label={`${this.props.intl.formatMessage({ id: 'app.search.yourwork.filter.type.weblink' })}
				${this.props.intl.formatMessage({ id: this.getTitleId() })}`}
			>
				{iconSource && (
					<div className='search-dropdown-quick-action-item-icon-container'>
						<img
							className='search-dropdown-quick-action-item-icon'
							src={iconSource}
							alt={`${this.props.intl.formatMessage({ id: 'app.search.icon' })} ${this.props.intl.formatMessage({ id: this.getTitleId() })}`}
						/>
					</div>
				)}
				<div className='search-dropdown-quick-action-item-text-title-container'>
					<FormattedMessage id={this.getTitleId()} />
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropDownQuickActionsItem));
